import React, { useCallback, useContext, useRef } from 'react';
import { PostScheduleLibraryView, PostScheduleLibraryViewProps } from './PostScheduleLibrary.view';
import { PostScheduleViewProps } from '../PostSchedule.view';
import {
  PostItemFragment,
  GetTimeslotsQuery,
  PostStatus,
  QueueCountsFieldsFragment,
  QueueSortOrder,
  QueueSortType
} from '../../../../models/graphql';
import { ContainerListItem } from '../../../../components/actions/ListItem/ListItem.view';
import { LibrarySort } from '../../../../constants/post';
import { AppContext } from '../../../../providers/AppProvider';
import { PostEditorContextEnum } from '../../../../models/post-editor';
import { LimitsReachedType } from '../PostScheduleList/PostScheduleList.apollo';
import { PostScheduleContext } from '../../../../providers/PostScheduleProvider';
import { statisticsConditionType } from '../../../../components/cards/helpers/getStatisticsCondition';
import { getBitlyMenuItems } from '../PostSchedule.controller';
import { useSubscriptionStatus } from 'hooks/useSubscriptionStatus';
import { OnboardingContext } from '../../../Onboarding/states/OnboardingProvider';
import { OnboardingStaticContent } from '../../../Onboarding/constants/content';

export type PostScheduleLibraryControllerProps = PostScheduleViewProps & {
  fetchMorePostItems: (scrollPosition: 'TOP' | 'BOTTOM') => void;
  posts: PostItemFragment[];
  queueCounts: QueueCountsFieldsFragment;
  limitsReached: LimitsReachedType;
  setSortOrder: (order: QueueSortOrder) => void;
  setSortType: (type: QueueSortType) => void;
  timeslots: GetTimeslotsQuery['workspace']['timeslots'];
};

export const PostScheduleLibraryController = (props: PostScheduleLibraryControllerProps): JSX.Element => {
  const { setPostEditorContext, setSuspendedModalOpen } = useContext(AppContext);
  const { deleteSinglePost, duplicateOrReschedulePost, queueCounts, limitsReached, showDetailsModalHandler } = props;
  const { setTriggerPostReFetch } = useContext(PostScheduleContext);
  const { showOnboarding, state: onboardingState } = useContext(OnboardingContext);
  const { version } = onboardingState;
  const disableDelete = OnboardingStaticContent[`v${version}`].disableDelete.posts;
  const leftColRef = useRef<HTMLDivElement>(null);
  const rightColRef = useRef<HTMLDivElement>(null);
  const { isTemporallySuspended } = useSubscriptionStatus();
  const { fetchMorePostItems } = props;

  const { setSortOrder, setSortType } = props;
  const sortOrderHandler = useCallback(
    (key: string) => {
      const sort = LibrarySort[key];

      if (sort) {
        setSortOrder(sort.order);
        setSortType(sort.type);
      }
    },
    [setSortOrder, setSortType]
  );

  const fetchMoreLibraryItems = useCallback(
    (scrollDirection: QueueSortOrder, scrollPosition: 'TOP' | 'BOTTOM') => {
      const hasReachedLimit =
        (scrollPosition === 'TOP' && limitsReached.upper) ||
        (scrollPosition === 'BOTTOM' && limitsReached.lower) ||
        (!scrollDirection && limitsReached.lower);

      if (!hasReachedLimit) {
        fetchMorePostItems(scrollPosition);
      }
    },
    [limitsReached, fetchMorePostItems]
  );

  const getLibraryActionMenuListItems = useCallback(
    (post: PostItemFragment, statConditions: statisticsConditionType): ContainerListItem[] => {
      let menu: ContainerListItem[];

      switch (post.status) {
        case PostStatus.ACTIVE:
          menu = [
            {
              label: 'Edit',
              action: () => {
                if (isTemporallySuspended) {
                  setSuspendedModalOpen(true);
                  return;
                }
                setPostEditorContext({
                  type: PostEditorContextEnum.edit,
                  postId: post.id,
                  onClose: success => {
                    if (success) {
                      setTriggerPostReFetch(post.id);
                    }
                  }
                });
              }
            }
          ];

          menu = [
            ...menu,
            ...[
              { label: 'Duplicate', action: () => duplicateOrReschedulePost(post, 'duplicate') },
              ...(statConditions.show
                ? [{ label: 'Detailed stats', action: () => showDetailsModalHandler(post.id) }]
                : []),
              ...getBitlyMenuItems(post.link),
              {
                label: 'Trash',
                action: () => deleteSinglePost(post.id),
                ...(disableDelete && showOnboarding && post.onboardingContent
                  ? { disabled: true, disabledTooltipText: 'You’ll be able to delete these demo posts later.' }
                  : {})
              }
            ]
          ];
          break;
        case PostStatus.PUBLISHED:
          menu = [
            { label: 'Reschedule', action: () => duplicateOrReschedulePost(post, 'reschedule') },
            ...(statConditions.show
              ? [{ label: 'Detailed stats', action: () => showDetailsModalHandler(post.id) }]
              : []),
            ...getBitlyMenuItems(post.link),
            { label: 'Duplicate', action: () => duplicateOrReschedulePost(post, 'duplicate') }
          ];
          break;
        default:
          menu = [
            { label: 'Reschedule', action: () => duplicateOrReschedulePost(post, 'reschedule') },
            ...getBitlyMenuItems(post.link),
            { label: 'Trash', action: () => deleteSinglePost(post.id) }
          ];
          break;
      }

      return menu;
    },
    [
      setPostEditorContext,
      setTriggerPostReFetch,
      duplicateOrReschedulePost,
      showDetailsModalHandler,
      showOnboarding,
      disableDelete,
      deleteSinglePost,
      isTemporallySuspended,
      setSuspendedModalOpen
    ]
  );

  const viewProps: PostScheduleLibraryViewProps = {
    ...props,
    leftColRef,
    rightColRef,
    hasReachedFetchLimit: limitsReached.upper,
    getLibraryActionMenuListItems,
    queueCounts,
    ...{
      fetchMoreLibraryItems,
      sortOrderHandler
    }
  };

  return <PostScheduleLibraryView {...viewProps} />;
};
